import React, { useState, useEffect, useCallback, useRef } from "react";
import { Store } from "../../../types";
import { Divider } from "@mui/material";
import Editor, { OnMount } from "@monaco-editor/react";
import { createLog, getStoreJSONSettings, putStoreJSONSettings } from "../../../lib/admin";
import { SpinnerCircularFixed } from "../../../assets/spinner/SpinnerCircularFixed";
import InfoWarningIcon from "../../../assets/InfoWarningIcon";
import StickySaveBar from "../../global/StickySaveBar";
import Notification from "../../global/Notification";
import { useHasScope } from "../../../utils/Permission";
import StoreSiteStatus, { SiteStatusKey } from "./StoreSiteStatus";
import settingsIcon from "../../../assets/management/braces.svg";

const parseSettings = (json: string): Record<string, any> | null => {
    try {
        const parsed = JSON.parse(json);
        return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const isSameSettings = (a: Record<string, any>, b: Record<string, any>) =>
    Object.keys(a).length === Object.keys(b).length
    && Object.keys(a).every((key) => key in b && JSON.stringify(a[key]) === JSON.stringify(b[key]));

type Props = {
    store: Store,
    hasUnsavedChanges: boolean,
    setHasUnsavedChanges: (flag: boolean) => void,
    hasBeenUpdated: boolean,
    setHasBeenUpdated: (flag: boolean) => void
}

export default function StoreShopifySettings({store, setHasUnsavedChanges, setHasBeenUpdated}: Props) {
    const [baseline, setBaseline] = useState("");
    const [jsonContent, setJsonContent] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ message: string | null; type: 'success' | 'error' }>({
        message: null,
        type: 'success',
    });
    const hasUpdateStoreSettings = useHasScope('update:jsonsettings');
    const editorRef = useRef<Parameters<OnMount>[0] | null>(null);

    const dismissSaveError = useCallback(() => setSaveError(null), []);
    const closeNotification = useCallback(() => setNotification({ message: null, type: 'success' }), []);

    const hasChanges = jsonContent !== baseline;
    const currentSettings = parseSettings(jsonContent);
    const siteStatusSettings = currentSettings ?? parseSettings(baseline);

    useEffect(() => {
        setHasUnsavedChanges(hasChanges);
    }, [hasChanges, setHasUnsavedChanges]);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        if(!hasUpdateStoreSettings) return;
        setIsLoading(true);
        try {
            const response = await getStoreJSONSettings(store.id.toString());
            const content = response.status === 204 || response.data === null
                ? "{}"
                : JSON.stringify(response.data, null, 2);
            setBaseline(content);
            setJsonContent(content);
            setLoadFailed(false);
        } catch (err) {
            setLoadFailed(true);
            setNotification({ message: 'Failed to load settings. Please try again later.', type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditorChange = (value: string | undefined) => {
        if (value !== undefined) {
            setJsonContent(value);
        }
    };

    const handleSave = async () => {
        if (!hasUpdateStoreSettings) {
            setSaveError("You do not have permission to update this store.");
            return;
        }
        setSaveError(null);
        setSaving(true);
        try {
            const response = await putStoreJSONSettings(store.id.toString(), jsonContent)
            if (!response.ok) {
                throw new Error("Failed to update settings");
            }
            createLog({
                storeId: store.id,
                entityType: 'storeSettings',
                section: 'settings',
                action: 'save',
                lastValue: { settings: baseline },
                newValue: { settings: jsonContent },
            });
            setBaseline(jsonContent);
            setNotification({ message: "Settings updated successfully", type: 'success' });
            setHasBeenUpdated(true);
        } catch (err) {
            setSaveError("Failed to update settings. Please try again later.");
        } finally {
            setSaving(false);
        }
    };

    const handleSiteStatusToggle = (key: SiteStatusKey, value: boolean) => {
        if (!currentSettings) return;
        const saved = parseSettings(baseline);
        const settings: Record<string, any> = { ...currentSettings };
        const other: SiteStatusKey = key === "siteDisabled" ? "siteMaintenance" : "siteDisabled";
        if (value && settings[other] === true) {
            if (saved && !(other in saved)) {
                delete settings[other];
            } else {
                settings[other] = false;
            }
        }
        const { [key]: _previous, ...rest } = settings;
        const next = !value && saved && !(key in saved) ? rest : { [key]: value, ...rest };
        setJsonContent(saved && isSameSettings(next, saved) ? baseline : JSON.stringify(next, null, 2));
        editorRef.current?.setScrollTop(0);
    };

    const handleCancel = () => {
        setJsonContent(baseline);
        setSaveError(null);
    };

    if (isLoading) {
        return (
            <div className="spinner-div spinnerReport">
                <SpinnerCircularFixed className='' size={35} thickness={140} speed={120} color="rgba(203, 216, 234, 1)" secondaryColor="rgba(0, 0, 0, 0.44)" />
            </div>
        );
    }

    return (
        <div className="sync-management">
            <div className="sync-header">
                <p>Shopify Stores/{store.friendlyName}</p>
                <h1><img src={settingsIcon} alt="" />Store Settings JSON</h1>
            </div>
            <Divider className="divider"/>
            {!loadFailed && (
                <div className="form-container">
                    <div className="warning-box">
                        <InfoWarningIcon></InfoWarningIcon>
                        <div className="content">
                            <p><strong>Important:</strong> The store settings feature allows you to modify various store behaviors without the need to deploy your store again.</p>
                            <p>The GET request is made directly from Shopify.</p>
                            <p>The POST is done in two steps:</p>
                            <ol>
                                <li>First, it saves directly to Shopify.</li>
                                <li>Then, it saves to the database because the sync has some logic that changes according to the settings.</li>
                            </ol>
                            <p>More info: <a href="https://retailsystem-dev.notion.site/Store-Settings-DB-70d06c4041514a429cf591af3161b69a" target="_blank" rel="noopener noreferrer">Store Settings | Notion</a></p>
                        </div>
                    </div>
                    <StoreSiteStatus
                        siteDisabled={siteStatusSettings?.siteDisabled === true}
                        siteMaintenance={siteStatusSettings?.siteMaintenance === true}
                        invalidJson={currentSettings === null}
                        disabled={saving}
                        onToggle={handleSiteStatusToggle}
                    />
                    <div className="store-settings-text">
                        <Editor
                            height="100%"
                            defaultLanguage="json"
                            value={jsonContent}
                            onChange={handleEditorChange}
                            onMount={(editor) => { editorRef.current = editor; }}
                            theme="vs-dark"
                            options={{
                                readOnly: saving,
                                minimap: { enabled: false },
                                scrollBeyondLastLine: false,
                                fontSize: 14,
                                tabSize: 2,
                                wordWrap: "on",
                                wrappingIndent: "same",
                                automaticLayout: true,
                                useShadowDOM: false,
                            }}
                        />
                    </div>
                </div>
            )}
            <StickySaveBar
                visible={hasChanges}
                saving={saving}
                onSave={handleSave}
                onCancel={handleCancel}
                alignWithSelector=".leftVertical"
                errorMessage={saveError ?? undefined}
                onErrorDismiss={dismissSaveError}
            />
            <Notification message={notification.message} type={notification.type} onClose={closeNotification} />
        </div>
    );
}